import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ImagingReview } from "@/components/common/ImagingReview";
import { QRCodeSVG } from "qrcode.react";
import { Upload, Smartphone, QrCode, Copy, ImageIcon, FileUp } from "lucide-react";
import { toast } from "sonner";

export function ImagingUploadPanel({ consultationId, onChange, compact = false }) {
  const [images, setImages] = useState([]);
  const [session, setSession] = useState(null);
  const fileRef = useRef(null);
  const token = localStorage.getItem("contaha_token");

  const load = async () => { try { const { data } = await api.get(`/imaging?consultation_id=${consultationId}`); setImages(data); } catch { /* ignore */ } };
  useEffect(() => { if (!consultationId) return; load(); const iv = setInterval(load, 4000); return () => clearInterval(iv); /* eslint-disable-next-line */ }, [consultationId]);
  useEffect(() => {
    const pending = images.reduce((n, im) => n + (im.review_items || []).filter((x) => x.status === "pending").length, 0);
    const analyzing = images.filter((im) => !im.ai_assessment && ["analyzing", undefined, null].includes(im.assessment_status)).length;
    onChange?.({ count: images.length, pending, analyzing });
    // eslint-disable-next-line
  }, [images]);

  const uploadFiles = async (files) => {
    for (const f of Array.from(files || [])) {
      try { const fd = new FormData(); fd.append("file", f); fd.append("consultation_id", consultationId); fd.append("image_type", /pdf/i.test(f.type) ? "Report" : "Other"); await api.post("/imaging/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch { toast.error(`Couldn't upload ${f.name}`); }
    }
    toast.success("Added to the case — AI is reading it now"); load();
  };
  const startPhone = async () => { try { const { data } = await api.post(`/consultations/${consultationId}/upload-session`); setSession(data); } catch { toast.error("Couldn't create the upload link"); } };
  const link = session ? `${window.location.origin}/m/upload/${session.token}` : "";

  return (
    <div data-testid="imaging-upload-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><ImageIcon className="h-4 w-4 text-[#00A85A]" /><span className="text-sm font-bold text-[#0B1512] dark:text-white">Documents & Imaging</span></div>
        <span className="text-xs text-[#8B9698]" data-testid="imaging-count">{images.length} attached</span>
      </div>
      {!compact && <p className="mt-1 text-xs text-[#8B9698]">Add OPG, IOPA, CBCT, clinical photos or PDF reports. AI reads each file; you accept, edit or reject every finding before analysis.</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="imaging-file-input" />
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="upload-from-computer" className="rounded-lg"><Upload className="mr-1.5 h-4 w-4" />Upload from computer</Button>
        <Button variant="outline" size="sm" onClick={startPhone} data-testid="upload-from-phone" className="rounded-lg"><Smartphone className="mr-1.5 h-4 w-4" />Upload from phone</Button>
      </div>
      {session && (
        <div className="mt-4 flex flex-col items-center gap-3 rounded-xl border border-dashed border-[#00A85A]/40 bg-[#00A85A]/5 p-4 sm:flex-row sm:gap-4" data-testid="qr-panel">
          <div className="rounded-lg bg-white p-2 shrink-0"><QRCodeSVG value={link} size={104} fgColor="#0B1512" /></div>
          <div className="text-center sm:text-left">
            <div className="flex items-center justify-center gap-1.5 text-sm font-bold text-[#0B1512] dark:text-white sm:justify-start"><QrCode className="h-4 w-4 text-[#00A85A]" />Scan with your phone camera</div>
            <p className="mt-1 text-xs text-[#8B9698]">Opens a secure upload page. Files appear here automatically. Link valid for 6 hours.</p>
            <button data-testid="copy-upload-link" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Upload link copied"); }} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A85A] hover:underline"><Copy className="h-3.5 w-3.5" />Copy link instead</button>
          </div>
        </div>
      )}
      {images.length > 0 && <div className="mt-4 text-left"><ImagingReview images={images} onUpdate={setImages} /></div>}
      {images.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="imaging-grid">
          {images.map((im) => (
            <div key={im.id} className="relative aspect-square overflow-hidden rounded-lg border border-black/5 bg-[#F4FAF6] dark:border-white/10 dark:bg-white/5">
              {im.content_type?.startsWith("image/")
                ? <img src={`${API}/files/${im.storage_path}?auth=${token}`} alt={im.image_type} className="h-full w-full object-cover" />
                : <div className="flex h-full w-full items-center justify-center"><FileUp className="h-6 w-6 text-[#8B9698]" /></div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
